import React from 'react';
import { getInAppBrowserName, isAndroidUserAgent } from 'utils/inAppBrowser';
import applicationInsights from 'utils/applicationInsights';

const SIGN_IN_URL = '/api/auth/social-login';

/**
 * buildAndroidChromeIntentUrl
 *
 * An `intent://` link is the only reliable way to force Android to hand a link to Chrome instead of
 * whatever in-app webview rendered it - a plain https:// link, an anchor tag, or window.open() all
 * stay inside the calling app's webview. No iOS equivalent exists; iOS in-app browsers can only be
 * escaped by the user themselves (the "..." / share menu's "Open in Safari"), which is why the iOS
 * branch below is a static instruction rather than a link.
 */
const buildAndroidChromeIntentUrl = () => {
  const { host } = window.location;
  return `intent://${host}${SIGN_IN_URL}#Intent;scheme=https;package=com.android.chrome;end`;
};

const trackGateEvent = (name, extra) => {
  applicationInsights.trackEvent({ name }, {
    inAppBrowser: getInAppBrowserName(window.navigator.userAgent),
    ...extra,
  });
};

const continueSameTab = () => {
  trackGateEvent('SignInGateContinueSameTab');
  window.location.href = SIGN_IN_URL;
};

const continueNewTab = () => {
  trackGateEvent('SignInGateContinueNewTab');
  // Opened directly from this button's onClick (a user gesture), which mobile popup blockers
  // require - this call would silently fail if it ran from an effect or on page load instead.
  window.open(SIGN_IN_URL, '_blank');
};

/**
 * InAppBrowserSignInGate
 *
 * Shown instead of Layout's normal instant redirect to Entra when NEXT_PUBLIC_LOGIN_IN_APP_BROWSER_GATE
 * is enabled and the visitor is on mobile. Two purposes:
 *  1. For a *detected* in-app browser (Facebook, Instagram, etc.) - offer an explicit path to the
 *     system browser before starting sign-in, since a password-reset code screen started inside an
 *     in-app browser is liable to disappear when the user backgrounds the app to read the code.
 *  2. Otherwise - a plain "Continue" button, so sign-in starts from a real click. This is what makes
 *     the NEXT_PUBLIC_LOGIN_NEW_TAB_EXPERIMENT prototype possible at all (task 3): the previous
 *     behavior redirected automatically with no click for window.open() to attach to.
 *
 * Undetected in-app browsers (iOS Gmail/Outlook - see utils/inAppBrowser.js) fall through to the
 * plain "Continue" button and get whichever behavior the new-tab flag selects; there's no way to
 * catch them at this layer.
 */
const InAppBrowserSignInGate = () => {
  const { userAgent } = window.navigator;
  const inAppBrowserName = getInAppBrowserName(userAgent);
  const newTabExperimentEnabled = process.env.NEXT_PUBLIC_LOGIN_NEW_TAB_EXPERIMENT === 'true';

  if (inAppBrowserName) {
    return (
      <div style={{
        textAlign: 'center', padding: '48px 24px', maxWidth: 420, margin: '0 auto',
      }}
      >
        <p style={{ fontSize: '11pt' }}>
          {"For a secure sign-in, please continue in your device's browser."}
        </p>
        {isAndroidUserAgent(userAgent) ? (
          <a
            href={buildAndroidChromeIntentUrl()}
            onClick={() => trackGateEvent('SignInGateOpenSystemBrowser', { platform: 'android' })}
            style={{ display: 'inline-block', margin: '16px 0' }}
          >
            {'Open in Chrome'}
          </a>
        ) : (
          <p style={{ fontSize: '10pt', color: '#555' }}>
            {'Tap the '}
            <strong>{'••• '}</strong>
            {'or share icon in this app and choose '}
            <strong>{'"Open in Safari"'}</strong>
            {', then return here.'}
          </p>
        )}
        <br />
        <button
          type='button'
          onClick={continueSameTab}
          style={{
            background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer',
          }}
        >
          {'Continue here instead'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <button type='button' onClick={newTabExperimentEnabled ? continueNewTab : continueSameTab}>
        {'Continue to sign in'}
      </button>
    </div>
  );
};

export default InAppBrowserSignInGate;
